export function resultsTransformer(data, queryName) {
    if (!data || !transformers[queryName]) { return data; }

    return transformers[queryName](data);
}

// TODO Transform known datetime fields into local datetime
// TODO Transform known booleans into Yes/No (could we move this to backend to minimize front-end transforms?)
const transformers = {
    getApplicationDetails: o => {
        o.details.history = applicationDetailsHistory(o.details.history);
        o.details.administratorDetails = applicationDetailsAdministratorDetails(jp(o.details.administratorDetails));
        o.details.highSchool = applicationDetailsHighSchool(o.details.highSchool);
        o.details.resumeLaterRubricValues = jp(o.details.resumeLaterRubricValues);
        o.configuration.rubric = jp(o.configuration.rubric);
        o.configuration.recommendationOptions = jp(o.configuration.recommendationOptions);
        o.configuration.administrativeNotes = jp(o.configuration.administrativeNotes);
        o.configuration.adminOverrideOptions = jp(o.configuration.adminOverrideOptions);
        return o;
    },
    getReport: o => {
        o.columns = jp(o.columns);
        o.items = jp(o.items);
        o.items = searchDetails(o.items);
        return o;
    },
    searchForAllData: o => {
        let results = jp(o.results);
        results = searchDetails(results);
        o.results = results;
        return o;
    },
    personSearch: o => {
        let results = jp(o.results);
        results = searchDetails(results);
        o.results = results;
        return o;
    },
    listAssignableApplications: o => {
        return transformManageApplications(o, 'assignableApplications');
    },
    listAssignedApplications: o => {
        return transformManageApplications(o, 'assignedApplications');
    }
};

function transformManageApplications(data, applicationType) {
    if (!data?.reviewer) return data;

    // Parse columns
    data.columns = jp(data.columns) || [];

    // Parse and transform applications
    const apps = jp(data.reviewer[applicationType]);
    if (Array.isArray(apps)) {
        data.reviewer[applicationType] = apps.map(item => {
            const transformed = { ...item };

            // Transform each property
            Object.keys(transformed).forEach(key => {
                const value = transformed[key];

                // Preserve raw boolean for isPendingProcessing; convert other booleans to Yes/No
                if (typeof value === 'boolean') {
                    if (key !== 'isPendingProcessing') {
                        transformed[key] = value ? 'Yes' : 'No';
                    }
                }
                // Handle dates - format if it's a date string
                else if (isDateString(value)) {
                    transformed[key] = dt(value);
                }
            });

            // Transform nested arrays
            if (Array.isArray(transformed.commentCodes)) {
                transformed.commentCodes = transformed.commentCodes.map(code => ({
                    ...code,
                    commentDate: dt(code.commentDate)
                }));
            }
            if (Array.isArray(transformed.decisionCommunications)) {
                transformed.decisionCommunications = transformed.decisionCommunications.map(dc => ({
                    ...dc,
                    completedDt: dt(dc.completedDt)
                }));
            }
            if (Array.isArray(transformed.meritScholarship)) {
                transformed.meritScholarship = transformed.meritScholarship.map(ms => ({
                    ...ms,
                    completedDt: dt(ms.completedDt)
                }));
            }
            if (Array.isArray(transformed.serviceIndicators)) {
                transformed.serviceIndicators = transformed.serviceIndicators.map(si => ({
                    ...si,
                    activationDt: dt(si.activationDt)
                }));
            }
            if (Array.isArray(transformed.studentGroups)) {
                transformed.studentGroups = transformed.studentGroups.map(sg => ({
                    ...sg,
                    effDt: dt(sg.effDt)
                }));
            }

            return transformed;
        });
    }

    return data;
}

function isDateString(value) {
    if (typeof value !== 'string') return false;
    // Check for common date patterns (ISO dates, timestamps, etc.)
    const dateRegex = /^\d{4}-\d{2}-\d{2}|^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}|^\d{13}$/;
    return dateRegex.test(value) && !isNaN(new Date(value).getTime());
}

function searchDetails(results) {
    if (Array.isArray(results)) {
        results = results.map(item => {
            if (Array.isArray(item.commentCodes)) {
                item.commentCodes = item.commentCodes.map(code => ({
                    ...code,
                    commentDate: dt(code.commentDate)
                }));
            }
            if (Array.isArray(item.decisionCommunications)) {
                item.decisionCommunications = item.decisionCommunications.map(dc => ({
                    ...dc,
                    completedDt: dt(dc.completedDt)
                }));
            }
            if (Array.isArray(item.meritScholarship)) {
                item.meritScholarship = item.meritScholarship.map(ms => ({
                    ...ms,
                    completedDt: dt(ms.completedDt)
                }));
            }
            if (Array.isArray(item.serviceIndicators)) {
                item.serviceIndicators = item.serviceIndicators.map(si => ({
                    ...si,
                    activationDt: dt(si.activationDt)
                }));
            }
            if (Array.isArray(item.studentGroups)) {
                item.studentGroups = item.studentGroups.map(sg => ({
                    ...sg,
                    effDt: dt(sg.effDt)
                }));
            }
            if (item.applicationDate) {
                item.applicationDate = dt(item.applicationDate);
            }
            if (item.admitDate) {
                item.admitDate = dt(item.admitDate);
            }
            if (item.admissionStatusDate) {
                item.admissionStatusDate = dt(item.admissionStatusDate);
            }
            if (item.presidentsScholarshipProgramDate) {
                item.presidentsScholarshipProgramDate = dt(item.presidentsScholarshipProgramDate);
            }
            if (item.decisionDate) {
                item.decisionDate = dt(item.decisionDate);
            }
            if (item.dateOfBirth) {
                item.dateOfBirth = dt(item.dateOfBirth);
            }
            if (item.evaluationDate) {
                item.evaluationDate = dt(item.evaluationDate);
            }
            if (item.studentRequestDate) {
                item.studentRequestDate = dt(item.studentRequestDate);
            }
            if (item.studentRequestUpdatedDatetime) {
                item.studentRequestUpdatedDatetime = dt(item.studentRequestUpdatedDatetime);
            }
            if (item.recommendationActualDatetime) {
                item.recommendationActualDatetime = dt(item.recommendationActualDatetime);
            }
            if (item.createdDatetime) {
                item.createdDatetime = dt(item.createdDatetime);
            }
            if (item.completedByDueDate) {
                item.completedByDueDate = dt(item.completedByDueDate);
            }
            if (item.submissionDatetime) {
                item.submissionDatetime = dt(item.submissionDatetime);
            }
            if (item.meritScholarship_completedDt_filter) {
                item.meritScholarship_completedDt_filter = dt(item.meritScholarship_completedDt_filter);
            }

            [
                "assignable",
                "categoryBsla",
                "categoryDance",
                "categoryMusic",
                "categoryRotc",
                "categoryYoungScholars",
                "earlyAction",
                "firstGen",
                "isAcceptanceFeePaid",
                "isEminence",
                "isGapYear",
                "isPendingProcessing",
                "isPresidentsScholarshipProgram",
                "lowSocioeconomic",
                "msp",
                "mspAwardee",
                "regularDecision",
                "scholars",
                "siblings",
                "studentRequestApproved",
                "studentRequestIsActive",
            ].forEach(key => setYesNoDashBool(item, key));
            
            return item;
        });
    }

    return results;
}

function applicationDetailsHistory(o) {
    if (Array.isArray(o)) {
        o.forEach(row => {
            row.assignTime = dttm(row.assignTime);
            row.submitTime = dttm(row.submitTime);
            row.rubricValues = jp(row.rubricValues);
            row.administrativeNotes = jp(row.administrativeNotes);
        });
    }

    return o;
}

function applicationDetailsAdministratorDetails(o = {}) {
    o.admissionStatusDate = dt(o.admissionStatusDate);
    o.admitDate = dt(o.admitDate);
    o.applicationDate = dt(o.applicationDate);
    o.studentRequestDate = dt(o.studentRequestDate);
    o.studentRequestUpdatedDate = dt(o.studentRequestUpdatedDate);
    o.siblings = o.siblings ? "Yes" : "No";
    o.isGapYear = o.isGapYear ? "Yes" : "No";
    o.isEminence = o.isEminence ? "Yes" : "No";
    o.msp = o.msp ? "Yes" : "No";
    o.mspAwardee = o.mspAwardee ? "Yes" : "No";
    o.orientationDate = dt(o.orientationDate);
    o.acceptanceFeePaidDate = dt(o.acceptanceFeePaidDate);
    o.isAcceptanceFeePaid = o.isAcceptanceFeePaid ? "Yes" : "No";
    o.meritScholarship = setDates(o.meritScholarship, "completedDt", dt);
    o.studentGroups = setDates(o.studentGroups, "effDt", dt);
    o.serviceIndicators = setDates(o.serviceIndicators, "activationDt", dt);
    o.decisionCommunications = setDates(o.decisionCommunications, "completedDt", dt);
    o.commentCodes = setDates(o.commentCodes, "commentDate", dt);
    o.publishDateTime = dt(o.publishDateTime);
    o.presidentsScholarshipProgramDate = dt(o.presidentsScholarshipProgramDate);
    o.isPresidentsScholarshipProgram = o.isPresidentsScholarshipProgram ? "Yes" : "No";
    return o;
}

function setDates(arr, field, converter) {
    if (Array.isArray(arr)) {
        return arr.map(item => ({
            ...item,
            [field]: converter(item[field])
        }));
    }
    return arr;
}

function applicationDetailsHighSchool(o = {}) {
    o.highSchoolGraduationDate = o.highSchoolGraduationDate === " - " ? o.highSchoolGraduationDate : dt(o.highSchoolGraduationDate);
    return o;
}

function jp(json) { return json && JSON.parse(json); }
function dttm(o) { return isValidDate(o) ? new Date(o).toLocaleString() : o; }
function dt(o) { return isValidDate(o) ? new Date(o).toLocaleDateString() : o; }
function isValidDate(s) { return  s != null &&  s !== "" && !isNaN(new Date(s)); }
function setYesNoDashBool(item, key) {
    if (!Object.keys(item).includes(key)) { return; } // Skip, key doesn't exist
    
    item[key] = yesNoDashBool(item[key]);
}
function yesNoDashBool(bool) { return typeof bool !== "boolean" ? "-" : (bool ? "Yes" : "No"); }